import * as installationApi from '@/api/installation.api';
import { trabajosParaGuardar } from '@/lib/trabajos-payload';

/**
 * El borrador de la configuración de la instalación y cómo se pasa de y a la API. Vive aquí y no
 * en la página para que el archivo de la página exporte solo componentes (Fast Refresh).
 */

export interface CatalogRowDraft {
  name: string;
  price: string;
  note: string;
  descripcion: string;
  aliases: string;
  imagenes: string;
}

/** Fila editable de un centro de operación (números como texto en el borrador). */
export interface CentroRowDraft {
  nombre: string;
  lat: string;
  lng: string;
  radioKm: string;
}

export interface Draft {
  businessName: string;
  currencyPrefix: string;
  slaHours: string;
  notionDatabaseId: string;
  agent: {
    name: string;
    description: string;
    model: string;
    maxTokens: string;
    temperature: string;
    systemPrompt: string;
  };
  catalog: CatalogRowDraft[];
  leadStatusLabels: Record<string, string>;
  editableFields: installationApi.CrmEditableField[];
  /** Sin editor en la página: viajan tal cual para que guardar no los borre. */
  minimoPorZona?: Record<string, number>;
  guion?: { presentacion: string; franjasZoom: string[] } | null;
  zonas: string;
  tiposServicio: string;
  centros: CentroRowDraft[];
  messages: installationApi.MessagesConfig;
  glosario: installationApi.Glosario;
  /** Catálogos y reglas del módulo Trabajos. */
  trabajos: installationApi.TrabajosConfig;
}

const csv = (list: string[] | undefined): string => (list ?? []).join(', ');
const uncsv = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export function fromConfig(cfg: installationApi.InstallationConfig): Draft {
  return {
    businessName: cfg.businessName,
    currencyPrefix: cfg.currencyPrefix,
    slaHours: String(cfg.slaHours),
    notionDatabaseId: cfg.notionDatabaseId ?? '',
    agent: {
      name: cfg.agent.name,
      description: cfg.agent.description,
      model: cfg.agent.model,
      maxTokens: String(cfg.agent.maxTokens),
      temperature: String(cfg.agent.temperature),
      systemPrompt: cfg.agent.systemPrompt,
    },
    catalog: cfg.catalog.map((m) => ({
      name: m.name,
      price: String(m.price),
      note: m.note ?? '',
      descripcion: m.descripcion ?? '',
      aliases: csv(m.aliases),
      imagenes: csv(m.imagenes),
    })),
    leadStatusLabels: { ...cfg.crm.leadStatusLabels },
    editableFields: cfg.crm.editableFields.map((f) => ({ ...f, ...(f.opciones ? { opciones: [...f.opciones] } : {}) })),
    minimoPorZona: cfg.crm.minimoPorZona ? { ...cfg.crm.minimoPorZona } : undefined,
    guion: cfg.crm.guion === undefined ? undefined : cfg.crm.guion && { ...cfg.crm.guion, franjasZoom: [...cfg.crm.guion.franjasZoom] },
    zonas: csv(cfg.crm.zonas),
    tiposServicio: csv(cfg.crm.tiposServicio),
    // `?? []` por si el server aún no conoce centros (config vieja en caché).
    centros: (cfg.crm.centros ?? []).map((c) => ({
      nombre: c.nombre,
      lat: String(c.lat),
      lng: String(c.lng),
      radioKm: String(c.radioKm),
    })),
    messages: { ...cfg.messages },
    // Copia profunda: el borrador se edita fila por fila y no debe mutar la
    // respuesta cacheada. `?? []` por si habla con un server viejo sin glosario.
    glosario: {
      terminos: (cfg.glosario?.terminos ?? []).map((t) => ({ ...t })),
      evitar: (cfg.glosario?.evitar ?? []).map((e) => ({ ...e })),
    },
    // Copias defensivas, igual que el glosario. `?? ` por un server viejo.
    trabajos: {
      cierreConValidacion: cfg.trabajos?.cierreConValidacion === true,
      modelos: (cfg.trabajos?.modelos ?? []).map((m) => ({ ...m })),
      formasPago: (cfg.trabajos?.formasPago ?? []).map((f) => ({ ...f })),
      nivelesResolucion: (cfg.trabajos?.nivelesResolucion ?? []).map((n) => ({ ...n })),
      causasRaiz: (cfg.trabajos?.causasRaiz ?? []).map((c) => ({ ...c })),
    },
  };
}

export function toPayload(
  d: Draft,
): Omit<installationApi.InstallationConfig, 'key' | 'logoKey' | 'catalogPdfKey' | 'isInternal'> {
  return {
    businessName: d.businessName.trim(),
    currencyPrefix: d.currencyPrefix.trim() || 'RD$',
    slaHours: Math.max(1, parseInt(d.slaHours, 10) || 2),
    notionDatabaseId: d.notionDatabaseId.trim() || null,
    agent: {
      name: d.agent.name.trim(),
      description: d.agent.description,
      model: d.agent.model.trim(),
      maxTokens: Math.max(1, parseInt(d.agent.maxTokens, 10) || 1024),
      temperature: Math.min(1, Math.max(0, parseFloat(d.agent.temperature) || 0.6)),
      systemPrompt: d.agent.systemPrompt,
    },
    catalog: d.catalog
      .filter((m) => m.name.trim())
      .map((m) => ({
        name: m.name.trim(),
        price: parseInt(m.price, 10) || 0,
        note: m.note.trim() || undefined,
        descripcion: m.descripcion.trim() || undefined,
        aliases: uncsv(m.aliases),
        imagenes: uncsv(m.imagenes),
      })),
    crm: {
      leadStatusLabels: d.leadStatusLabels,
      editableFields: d.editableFields.filter((f) => f.key.trim()),
      // Revisión final A1-1: ia/tipo/opciones viajan dentro de cada campo y
      // estos dos se reenvían tal cual; omitidos, el server conserva los suyos.
      ...(d.minimoPorZona !== undefined ? { minimoPorZona: d.minimoPorZona } : {}),
      ...(d.guion !== undefined ? { guion: d.guion } : {}),
      zonas: uncsv(d.zonas),
      tiposServicio: uncsv(d.tiposServicio),
      // Solo filas completas y numéricamente válidas; radio inválido → 1 km.
      centros: d.centros
        .map((c) => ({
          nombre: c.nombre.trim(),
          lat: Number(c.lat),
          lng: Number(c.lng),
          radioKm: Number(c.radioKm) > 0 ? Number(c.radioKm) : 1,
        }))
        .filter(
          (c) =>
            c.nombre !== '' &&
            Number.isFinite(c.lat) &&
            Math.abs(c.lat) <= 90 &&
            Number.isFinite(c.lng) &&
            Math.abs(c.lng) <= 180,
        ),
    },
    messages: d.messages,
    // Las filas a medio escribir no viajan: el server las descartaría igual.
    glosario: {
      terminos: d.glosario.terminos
        .map((t) => ({ ...t, termino: t.termino.trim(), significa: t.significa.trim() }))
        .filter((t) => t.termino && t.significa),
      evitar: d.glosario.evitar
        .map((e) => ({ no: e.no.trim(), si: e.si.trim() }))
        .filter((e) => e.no && e.si),
    },
    // TODOS los catálogos viajan siempre: uno olvidado se borraría al guardar.
    trabajos: trabajosParaGuardar(d.trabajos),
  };
}
